/**
 * Arrow controls for a scroll-snap track.
 *
 * The track scrolls natively on touch and trackpad, so this only adds the
 * buttons on top. Expects one [data-track] child and any number of
 * [data-direction] buttons whose value is -1 or 1.
 *
 * This is a classic (non-module) script and some pages include it more than
 * once (e.g. a media-strip section AND a reviews section). Guarding the whole
 * definition — class declaration included — makes a second execution a no-op
 * instead of throwing "Identifier 'SnapCarousel' has already been declared",
 * which previously aborted the deferred-script phase and broke later scripts.
 */
if (!customElements.get('snap-carousel')) {
  class SnapCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.arrows = Array.from(this.querySelectorAll('[data-direction]'));
      this.arrows.forEach((arrow) => {
        arrow.addEventListener('click', () => {
          this.go(Number(arrow.dataset.direction));
        });
      });

      this.track.addEventListener('scroll', () => this.queueSync(), { passive: true });
      window.addEventListener('resize', () => this.queueSync(), { passive: true });

      // The arrows' enabled state depends on the track overflowing, which is
      // only true once the slides have their final size. Fonts and images can
      // settle after connectedCallback, and on a phone no resize event ever
      // follows to correct it, so watch the track itself.
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.queueSync());
        this.observer.observe(this.track);
      }

      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /** Coalesce the scroll/resize bursts into one measurement per frame. */
    queueSync() {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.sync();
      });
    }

    /**
     * Scroll offset of every slide, measured from the start of the track's
     * scrollable content. getBoundingClientRect() is relative to the viewport,
     * so it already has the current scroll subtracted out — scrollLeft has to
     * be added back to get a stable, scroll-independent offset. Measuring per
     * slide (instead of assuming a fixed step) keeps one tap = one slide even
     * when slides differ in width or the gap changes.
     */
    positions() {
      const trackLeft = this.track.getBoundingClientRect().left;
      const scrolled = this.track.scrollLeft;
      return Array.from(this.track.children).map((slide) =>
        Math.round(slide.getBoundingClientRect().left - trackLeft + scrolled)
      );
    }

    /** Index of the slide the track is currently resting on. */
    index(positions) {
      const current = this.track.scrollLeft;
      let index = 0;
      positions.forEach((position, i) => {
        if (Math.abs(position - current) < Math.abs(positions[index] - current)) index = i;
      });
      return index;
    }

    go(direction) {
      const positions = this.positions();
      if (!positions.length) return;

      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const next = Math.min(positions.length - 1, Math.max(0, this.index(positions) + direction));

      this.track.scrollTo({
        left: Math.min(Math.max(positions[next], 0), max),
        behavior: 'smooth',
      });
    }

    sync() {
      // A track that is not overflowing has nothing to page through.
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      const idle = max <= 0;

      this.arrows.forEach((arrow) => {
        const forward = Number(arrow.dataset.direction) > 0;
        arrow.disabled = idle || (forward ? this.track.scrollLeft >= max : this.track.scrollLeft <= 0);
      });
    }
  }

  customElements.define('snap-carousel', SnapCarousel);
}
